<template>
    <div>
        <div class="ui-list-bar">
            <p class="ui-subtitle" style="margin: 0;">
                Registro automático de cada solicitação, lembrete, cobrança, envio e conferência. Serve de prova de que a documentação foi pedida, mesmo quando não é entregue.
            </p>
            <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                <v-btn v-if="$allowed('rma.request')" outlined :disabled="!!competence.collection_stopped_at || (!!competence.requested_at && !competence.counts.missing)" @click="openEmail(null)">
                    <v-icon left small>mdi-email-fast-outline</v-icon>{{ remindLabel }}
                </v-btn>
                <v-btn v-if="(!competence.collection_stopped_at) && $allowed('rma.collection')" outlined class="ui-btn-danger" :loading="busy === 'stop'" @click="setCollection(true)">Encerrar cobrança</v-btn>
                <v-btn v-else-if="$allowed('rma.collection')" outlined :loading="busy === 'resume'" @click="setCollection(false)">Retomar cobrança</v-btn>
                <v-btn outlined :disabled="!events.length" @click="exportHistory"><v-icon left small>mdi-microsoft-excel</v-icon>Exportar</v-btn>
                <v-btn color="primary" depressed :loading="busy === 'dossier'" @click="openDossier"><v-icon left small>mdi-file-pdf-box</v-icon>Dossiê (PDF)</v-btn>
            </div>
        </div>

        <section v-if="drafts.length" class="rma-drafts mb-4">
            <div class="rma-drafts__title"><v-icon small color="warning">mdi-email-edit-outline</v-icon> {{ drafts.length === 1 ? "1 e-mail aguardando revisão" : `${drafts.length} e-mails aguardando revisão` }}</div>
            <div v-for="draft in drafts" :key="draft.id" class="rma-drafts__row">
                <div style="min-width: 0; flex: 1;">
                    <div class="ui-cell-main text-truncate">{{ draft.type_label }} · {{ draft.subject }}</div>
                    <div class="ui-cell-sub">
                        {{ draft.origin === "system" ? "Preparado automaticamente" : "Rascunho salvo" }} em {{ formatDateTime(draft.updated_at || draft.created_at) }} · ainda não enviado à devedora
                    </div>
                </div>
                <v-btn v-if="$allowed('rma.request')" small text class="ui-btn-danger" :loading="busy === `discard-${draft.id}`" @click="discardDraft(draft)">Descartar</v-btn>
                <v-btn v-if="$allowed('rma.request')" small color="primary" depressed @click="openEmail(draft.type)"><v-icon left small>mdi-pencil-outline</v-icon>Revisar e enviar</v-btn>
            </div>
        </section>

        <rma-email-dialog v-model="emailDialog.open" :competence="competence" :type="emailDialog.type" @saved="onEmailSaved" />

        <v-row>
            <v-col cols="12" md="5">
                <ui-section title="Linha do tempo" :description="`Competência ${competence.label}`">
                    <div v-if="loading" class="ui-muted ui-small">Carregando...</div>
                    <v-timeline v-else-if="events.length" dense align-top class="rma-timeline">
                        <v-timeline-item v-for="event in events" :key="event.id" small fill-dot :color="eventColor(event)" :icon="eventIcon(event)">
                            <div class="ui-cell-main">{{ event.label }}</div>
                            <div class="ui-cell-sub">{{ formatDateTime(event.created_at) }} · {{ event.actor_name || actorFallback(event) }}</div>
                            <div v-if="eventDetail(event)" class="ui-small mt-1">{{ eventDetail(event) }}</div>
                        </v-timeline-item>
                    </v-timeline>
                    <ui-empty v-else icon="mdi-timeline-outline" text="Nada registrado ainda." />
                </ui-section>
            </v-col>
            <v-col cols="12" md="7">
                <ui-section title="E-mails enviados" description="Situação de entrega informada pelo provedor (atualizada automaticamente)." flush>
                    <v-data-table :headers="emailHeaders" :items="emails" :loading="loading" disable-sort :items-per-page="25"
                        no-data-text="Nenhum e-mail enviado nesta competência" loading-text="Carregando..."
                        :footer-props="{ itemsPerPageOptions: [25, 50, 100], itemsPerPageText: 'Itens por página:' }">
                        <template v-slot:[`item.created_at`]="{ item }"><span class="ui-nowrap ui-muted">{{ formatDateTime(item.created_at) }}</span></template>
                        <template v-slot:[`item.kind`]="{ item }"><span class="ui-small">{{ kindLabels[item.kind] || item.kind }}</span></template>
                        <template v-slot:[`item.recipient`]="{ item }">
                            <div class="ui-cell-main" style="font-weight: 400;">{{ item.recipient }}</div>
                            <div v-if="item.recipient_name" class="ui-cell-sub">{{ item.recipient_name }}</div>
                        </template>
                        <template v-slot:[`item.status`]="{ item }">
                            <ui-badge set="rma-email" :status="item.status" />
                            <div v-if="item.error_message" class="ui-small" style="color: #b91c1c; max-width: 220px;">{{ item.error_message }}</div>
                        </template>
                    </v-data-table>
                </ui-section>
            </v-col>
        </v-row>

        <ui-section class="mt-5" title="Acessos da devedora" :description="accessDescription" flush>
            <div v-if="access.people.length" class="rma-access-people">
                <div v-for="person in access.people" :key="person.id" class="rma-access-person">
                    <v-icon small :color="person.accesses_in_period ? 'success' : 'error'">{{ person.accesses_in_period ? "mdi-account-check-outline" : "mdi-account-cancel-outline" }}</v-icon>
                    <div style="min-width: 0;">
                        <div class="ui-cell-main text-truncate">{{ person.name }}<span v-if="!person.active" class="ui-muted"> · inativo</span></div>
                        <div class="ui-cell-sub text-truncate">{{ person.email }}</div>
                        <div class="ui-small" :style="{ color: person.accesses_in_period ? '' : '#b42318' }">
                            {{ person.accesses_in_period ? `${person.accesses_in_period} acesso(s) no período · último em ${formatDateTime(person.last_access_at)}` : "Não acessou no período" }}
                        </div>
                    </div>
                </div>
            </div>
            <div v-else-if="!loading" class="ui-small ui-muted pa-4">O caso não tem usuários da recuperanda cadastrados.</div>
            <v-data-table v-if="access.logs.length" :headers="accessHeaders" :items="access.logs" disable-sort :items-per-page="10"
                :footer-props="{ itemsPerPageOptions: [10, 25, 50], itemsPerPageText: 'Itens por página:' }">
                <template v-slot:[`item.created_at`]="{ item }"><span class="ui-nowrap ui-muted">{{ formatDateTime(item.created_at) }}</span></template>
                <template v-slot:[`item.name`]="{ item }">
                    <div class="ui-cell-main" style="font-weight: 400;">{{ item.name || "—" }}</div>
                    <div class="ui-cell-sub">{{ item.email }}</div>
                </template>
                <template v-slot:[`item.event_label`]="{ item }"><span class="ui-small">{{ item.event_label }}</span></template>
                <template v-slot:[`item.ip`]="{ item }"><span class="ui-small ui-muted">{{ item.ip || "—" }}</span></template>
            </v-data-table>
        </ui-section>
    </div>
</template>

<script>
import axios from "axios";
import { downloadXlsxSheets } from "@/common/util/export-xlsx.util";
import RmaEmailDialog from "@/components/rma/RmaEmailDialog.vue";
import { EMAIL_KIND_LABELS, REVIEW_LABELS, formatDate, formatDateTime, notifyError, notifySuccess, notifyWarning } from "@/components/rma/rma.util";

const ICONS = {
    competence_opened: "mdi-calendar-plus",
    request_sent: "mdi-email-send-outline",
    reminder_sent: "mdi-bell-ring-outline",
    deadline_missed: "mdi-calendar-alert",
    overdue_sent: "mdi-email-alert-outline",
    file_uploaded: "mdi-upload",
    file_removed: "mdi-file-remove-outline",
    item_accepted: "mdi-check",
    item_returned: "mdi-undo-variant",
    item_reopened: "mdi-backup-restore",
    item_not_applicable: "mdi-minus-circle-outline",
    completed: "mdi-check-all",
    collection_stopped: "mdi-pause",
    collection_resumed: "mdi-play",
    archive_downloaded: "mdi-folder-zip-outline",
    pending_reinformed: "mdi-file-undo-outline",
    item_reviewed: "mdi-clipboard-alert-outline",
    email_draft_saved: "mdi-email-edit-outline",
    email_draft_discarded: "mdi-email-remove-outline",
    clarification_requested: "mdi-comment-question-outline",
    clarification_answered: "mdi-comment-check-outline",
    clarification_closed: "mdi-comment-remove-outline",
    share_link_created: "mdi-link-plus",
    share_link_revoked: "mdi-link-off",
    share_link_closed: "mdi-link-variant",
};

// Linha do tempo e e-mails da competencia (aba Historico do caso).
export default {
    name: "RmaTimeline",

    components: { RmaEmailDialog },

    props: {
        competence: { type: Object, required: true },
    },

    data: () => ({
        loading: false,
        busy: null,
        events: [],
        emails: [],
        drafts: [],
        emailDialog: { open: false, type: null },
        access: { people: [], logs: [], from: null, tracking_since: null },
        accessHeaders: [
            { text: "Data", value: "created_at", width: 150 },
            { text: "Usuário", value: "name" },
            { text: "Ação", value: "event_label" },
            { text: "IP", value: "ip", width: 140 },
        ],
        kindLabels: EMAIL_KIND_LABELS,
        emailHeaders: [
            { text: "Data", value: "created_at", width: 130 },
            { text: "Tipo", value: "kind", width: 130 },
            { text: "Destinatário", value: "recipient" },
            { text: "Situação", value: "status", width: 170 },
        ],
    }),

    computed: {
        accessDescription() {
            const from = this.access.from ? `desde ${formatDateTime(this.access.from)}` : "";
            const since = this.access.tracking_since ? ` Registro iniciado em ${formatDate(this.access.tracking_since)}.` : "";
            return `Logins, aberturas do painel e do checklist pelos usuários da recuperanda ${from}.${since}`;
        },

        remindLabel() {
            if (!this.competence.requested_at) return "Enviar solicitação";
            return this.competence.overdue ? "Cobrar agora" : "Enviar lembrete";
        },
    },

    created() {
        this.load();
    },

    methods: {
        formatDateTime,

        async load() {
            this.loading = true;
            try {
                const result = await axios.get(`/api/admin/rma/competences/${this.competence.id}/events`);
                this.events = [...result.data.content.events].reverse();
                this.emails = [...result.data.content.emails].reverse();
                this.access = result.data.content.access || { people: [], logs: [], from: null, tracking_since: null };
                this.drafts = result.data.content.drafts || [];
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        eventIcon(event) {
            return ICONS[event.type] || "mdi-circle-small";
        },

        eventColor(event) {
            if (/falha|sem destinat/.test(event.label)) return "error";
            if (["deadline_missed", "item_returned", "collection_stopped", "pending_reinformed", "item_reviewed", "email_draft_saved"].includes(event.type)) return "warning";
            if (["item_accepted", "completed", "clarification_answered"].includes(event.type)) return "success";
            if (["file_uploaded", "item_not_applicable"].includes(event.type)) return "info";
            return "primary";
        },

        actorFallback(event) {
            if (event.actor_type === "share_link") return "Link de envio";
            return event.actor_type === "admin" ? "AJ" : event.actor_type === "client_admin" ? "Recuperanda" : "Plataforma";
        },

        eventDetail(event) {
            const p = event.payload || {};
            if (["request_sent", "reminder_sent", "overdue_sent"].includes(event.type)) {
                if (p.no_recipients) return "Nenhum usuário da recuperanda cadastrado para receber.";
                const parts = [`${p.sent || 0} de ${(p.recipients || []).length} destinatário(s)`];
                if (p.copied) parts.push(`${p.copied} cópia(s) interna(s)`);
                if (p.missing) parts.push(`${p.missing} documento(s) pendente(s)`);
                if (p.manual) parts.push("enviado manualmente");
                if (p.reviewed) parts.push("texto revisado");
                return parts.join(" · ");
            }
            if (event.type === "share_link_created") return [p.label ? `para ${p.label}` : null, p.email || null, p.days ? `vale ${p.days} dia(s)` : null, `até ${formatDateTime(p.expires_at)}`].filter(Boolean).join(" · ");
            if (event.type === "share_link_revoked") return p.label ? `link de ${p.label}` : "";
            if (event.type === "share_link_closed") return `${p.files || 0} arquivo(s) enviado(s) pelo link`;
            if (event.type === "competence_opened") return `${p.items || 0} documentos no checklist · prazo ${formatDate(p.due_date)}`;
            if (event.type === "deadline_missed") return `Prazo ${formatDate(p.due_date)} · ${p.missing || 0} documento(s) pendente(s)`;
            if (["file_uploaded", "file_removed"].includes(event.type)) return [p.document, p.name].filter(Boolean).join(" · ") + (p.after_deadline ? " · após o prazo" : "");
            if (event.type === "item_returned") return [p.document, REVIEW_LABELS[p.review], p.message, p.reinformed ? "reinformado" : null].filter(Boolean).join(" · ");
            if (event.type === "item_reviewed") return [p.document, REVIEW_LABELS[p.review], "sem nova cobrança", p.message].filter(Boolean).join(" · ");
            if (event.type === "clarification_requested") {
                const parts = [p.document || "Geral", p.question];
                if (p.no_recipients) parts.push("sem destinatário");
                else parts.push(`e-mail a ${p.sent || 0} de ${(p.recipients || []).length} destinatário(s)`);
                return parts.filter(Boolean).join(" · ");
            }
            if (event.type === "clarification_answered") return [p.document || "Geral", p.answer].filter(Boolean).join(" · ");
            if (event.type === "clarification_closed") return [p.document || "Geral", p.answered ? "respondido" : "sem resposta"].filter(Boolean).join(" · ");
            if (["email_draft_saved", "email_draft_discarded"].includes(event.type)) {
                const origin = event.type === "email_draft_saved" ? (p.origin === "system" ? "preparado automaticamente" : (p.updated ? "rascunho atualizado" : "rascunho salvo")) : "";
                return [p.email_type_label, p.subject, origin].filter(Boolean).join(" · ");
            }
            if (event.type === "pending_reinformed") {
                const parts = [`${p.items || 0} documento(s): ${(p.documents || []).join(", ")}`];
                if (p.no_recipients) parts.push("nenhum usuário da recuperanda para receber");
                else parts.push(`e-mail a ${p.sent || 0} de ${(p.recipients || []).length} destinatário(s)`);
                if (p.collection_resumed) parts.push("cobrança retomada");
                if (p.message) parts.push(p.message);
                return parts.join(" · ");
            }
            if (event.type === "item_not_applicable") return [p.document, p.justification].filter(Boolean).join(" · ");
            if (["item_accepted", "item_reopened"].includes(event.type)) return p.document || "";
            if (event.type === "archive_downloaded") return `${p.files || 0} arquivo(s) · ${p.document ? `${p.document} · ` : ""}${p.from}${p.to && p.to !== p.from ? ` a ${p.to}` : ""}`;
            return "";
        },

        openEmail(type) {
            this.emailDialog = { open: true, type };
        },

        onEmailSaved() {
            this.load();
            this.$emit("changed");
        },

        async discardDraft(draft) {
            const confirmed = await this.$dialog.confirm({
                title: "Descartar rascunho",
                text: `O rascunho "${draft.type_label}" não será enviado. O descarte fica registrado no histórico. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Descartar", color: "error" } },
            });
            if (!confirmed) return;
            this.busy = `discard-${draft.id}`;
            try {
                await axios.post(`/api/admin/rma/email-drafts/${draft.id}/discard`);
                notifySuccess(this, "Rascunho descartado.");
                this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = null;
            }
        },

        async openDossier() {
            this.busy = "dossier";
            try {
                const result = await axios.get(`/api/admin/rma/competences/${this.competence.id}/dossier`, { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([result.data], { type: "application/pdf" }));
                window.open(url, "_blank");
                setTimeout(() => window.URL.revokeObjectURL(url), 60000);
            } catch (error) {
                notifyWarning(this, "Não foi possível gerar o dossiê. Tente novamente.");
            } finally {
                this.busy = null;
            }
        },

        async setCollection(stopped) {
            if (stopped) {
                const confirmed = await this.$dialog.confirm({
                    title: "Encerrar cobrança",
                    text: "Os lembretes e cobranças automáticos desta competência param. A recuperanda ainda pode enviar documentos. Confirma?",
                    actions: { false: "Cancelar", true: { text: "Encerrar", color: "error" } },
                });
                if (!confirmed) return;
            }
            this.busy = stopped ? "stop" : "resume";
            try {
                await axios.post(`/api/admin/rma/competences/${this.competence.id}/${stopped ? "stop-collection" : "resume-collection"}`);
                notifySuccess(this, stopped ? "Cobrança encerrada." : "Cobrança retomada.");
                this.$emit("changed");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = null;
            }
        },

        exportHistory() {
            downloadXlsxSheets([
                {
                    name: "Linha do tempo",
                    rows: [...this.events].reverse().map((event) => ({
                        Data: formatDateTime(event.created_at),
                        Evento: event.label,
                        Autor: event.actor_name || this.actorFallback(event),
                        Detalhe: this.eventDetail(event),
                    })),
                },
                {
                    name: "E-mails",
                    rows: [...this.emails].reverse().map((email) => ({
                        Data: formatDateTime(email.created_at),
                        Tipo: this.kindLabels[email.kind] || email.kind,
                        Destinatário: email.recipient,
                        Nome: email.recipient_name || "",
                        Assunto: email.subject,
                        Situação: email.status,
                        Erro: email.error_message || "",
                    })),
                },
            ], `Historico_RMA_${this.competence.key}`);
        },
    },
};
</script>

<style scoped>
.rma-timeline {
    padding-top: 0;
}

.rma-drafts {
    border: 1px solid #f0d9a8;
    background: #fff8eb;
    border-radius: 10px;
    padding: 12px 16px;
}

.rma-drafts__title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: #8a5a00;
    margin-bottom: 6px;
}

.rma-drafts__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    border-top: 1px solid #f3e4c3;
}

.rma-access-people {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 10px;
    padding: 14px 16px;
}

.rma-access-person {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #ebe9f1;
    border-radius: 10px;
}
</style>
